// AdminSeriesPage.tsx — (TF-6b-2; design §2) Admin → Series: the list. From the engine's list once it has one; until then
// (LIST_NOT_AVAILABLE) the series opened in THIS browser, each read from the engine, and the page says why. A row that
// can't be read says so; it is never dropped.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PageContainer, TfCard, Btn } from "@/components/v2/primitives.tsx";
import { engineSeriesApi } from "@/api/engine-series.ts";
import { seriesHistory, refusalText } from "@/lib/series.ts";

type Row = { id: string; title: string; patternId?: string; status?: string; pilot?: string; posted?: number; planned?: number; err?: string };
const storage = () => { try { return window.localStorage; } catch { return null; } };   // catch-reason: no storage → no history

export function AdminSeriesPage() {
  const [rows, setRows] = useState<Row[] | null>(null);
  const [fallback, setFallback] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    engineSeriesApi.list().then((r) => setRows(r.series.map((s) => ({ id: s.id, title: s.title, patternId: s.patternId, status: s.status, pilot: s.pilotStatus ?? undefined,
      posted: s.episodesPosted, planned: s.episodesPlanned }))))
      .catch(async (e) => {   // catch-reason: LIST_NOT_AVAILABLE → this browser's history; anything else is shown
        if ((e as { code?: string }).code !== "LIST_NOT_AVAILABLE") { setErr(refusalText(e)); return; }
        setFallback(true);
        const seen = seriesHistory(storage());
        setRows(await Promise.all(seen.map(async (h): Promise<Row> => {
          try {
            const s = await engineSeriesApi.get(h.id);
            const eps = s.episodes ?? [];
            return { id: s.id, title: s.title, patternId: s.patternId, status: s.status, pilot: eps.find((x) => x.seq === 0)?.status,
              posted: eps.filter((x) => x.seq > 0 && x.status === "posted").length, planned: eps.filter((x) => x.seq > 0).length };
          } catch (x) { return { id: h.id, title: h.title, err: refusalText(x) }; }   // catch-reason: the row says it couldn't be read
        })));
      });
  }, []);
  return (
    <PageContainer title="Series" subtitle="Growth series on tubeforge:growth: the pilot, its posts and traction, then the episodes."
      action={<Link to="/admin/series/new"><Btn>New series</Btn></Link>}>
      {fallback && <TfCard><div style={{ fontSize: 13 }}>Showing the series opened in this browser. The engine can't list series yet.</div></TfCard>}
      {err && <div style={{ color: "#b91c1c", fontSize: 13, margin: "8px 0" }}>The series couldn't be read: {err}</div>}
      {rows === null && !err && <div className="tf-muted">Reading…</div>}
      {rows && rows.length === 0 && <div className="tf-muted" style={{ marginTop: 12 }}>{fallback ? "No series opened in this browser yet." : "No series yet."}</div>}
      {rows && rows.length > 0 && (
        <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 12, fontSize: 14 }}>
          <thead><tr style={{ textAlign: "left" }}><th>Title</th><th>Pattern</th><th>Status</th><th>Pilot</th><th>Episodes posted / planned</th></tr></thead>
          <tbody>{rows.map((r) => (
            <tr key={r.id} style={{ borderTop: "1px solid var(--tf-border, #e5e7eb)" }}>
              <td><Link to={`/admin/series/${r.id}`}>{r.title}</Link></td>
              {r.err ? <td colSpan={4} style={{ color: "#b91c1c" }}>couldn't be read: {r.err}</td> : <>
                <td>{r.patternId}</td><td>{r.status}</td><td>{r.pilot ?? "—"}</td>
                <td>{r.posted ?? "—"} / {r.planned ?? "—"}</td></>}
            </tr>))}</tbody>
        </table>)}
    </PageContainer>
  );
}
